
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo } from 'react';
import { Card } from '../ui/Card';
import { useWallet } from '../../hooks/useWallet';
import { AreaChart } from '../charts/AreaChart';
import { PieChart } from '../charts/PieChart';
import { AssetRow } from './wallet/AssetRow';
import { ArrowUpRight, Search, PieChart as PieIcon, Database, TrendingUp, RefreshCw, BarChart2 } from 'lucide-react';
import { Button } from '../ui/Button';

const ASSET_COLORS: Record<string, string> = {
    'SUI': '#3B82F6',   // Blue 500
    'GHOST': '#C5A059', // Nobel Gold
    'veGHOST': '#A08040', // Darker Gold
    'wGHOST': '#9333EA', // Purple 600
    'SOL': '#9945FF',   // Solana Purple
    'USDC': '#2775CA',  // USDC Blue
    'CETUS': '#06B6D4', // Cyan 500
    'SPL': '#10B981'    // Emerald 500
};

export const PortfolioView: React.FC = () => {
    const { balances, refreshBalances } = useWallet();

    const portfolioValue = useMemo(() => balances.reduce((acc, token) => acc + token.balance * token.priceUsd, 0), [balances]);

    // Mock historical data for the chart
    const historicalData = useMemo(() => {
        const data = [];
        let value = portfolioValue * 0.8;
        for (let i = 0; i < 12; i++) {
            value += (Math.random() - 0.45) * value * 0.05;
            data.push(Math.max(0, value));
        }
        return data;
    }, [portfolioValue]);

    const pieChartData = useMemo(() => {
        const data = balances
            .filter(b => b.balance * b.priceUsd > 0)
            .map(token => ({
                name: token.symbol,
                value: token.balance * token.priceUsd,
                color: ASSET_COLORS[token.symbol] || ASSET_COLORS[token.type] || '#8884d8',
                icon: token.icon
            }))
            .sort((a, b) => b.value - a.value);
            
        if (data.length === 0) {
            return [{ name: 'Empty', value: 1, color: '#2D3142', icon: '' }];
        }
        return data;
    }, [balances]);

    return (
        <div className="space-y-4 animate-fade-in pb-12">
            {/* 1. Precise Header Card (Encapsulated) */}
            <Card className="bg-stone-50 dark:bg-[#0A0B0F] text-[#0A0B0F] dark:text-white p-6 md:p-8 shadow-smooth relative overflow-hidden border-stone-200 dark:border-white/10 transition-colors duration-300 rounded-sm">
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="text-center md:text-left">
                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-stone-500 mb-1">Vault Net Valuation</div>
                        <div className="text-4xl sm:text-5xl md:text-6xl font-serif font-medium tracking-tighter leading-none">
                            ${portfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="mt-4 flex items-center justify-center md:justify-start gap-4">
                             <div className="flex items-center gap-2 text-sm font-bold text-emerald-600 dark:text-emerald-500 bg-emerald-500/5 px-4 py-2 rounded-sm border border-emerald-500/10">
                                <ArrowUpRight size={18}/> +$1,203.45 (10.2%)
                            </div>
                            <span className="text-[9px] tracking-widest font-black text-stone-400 uppercase">24H Market Pulse</span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={refreshBalances} icon={<RefreshCw size={12}/>} className="bg-white dark:bg-[#0A0B0F]">Sync Ledger</Button>
                    </div>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-nobel-gold/5 blur-[100px] pointer-events-none rounded-full" />
            </Card>
            
            {/* 2. Analytics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
                <div className="lg:col-span-8">
                    <Card noPadding className="h-full flex flex-col border-stone-200 dark:border-white/5 overflow-hidden">
                        <div className="p-6 border-b border-stone-50 dark:border-white/[0.02] flex justify-between items-center bg-stone-50/50 dark:bg-white/[0.01]">
                            <div className="space-y-0.5">
                                <h3 className="font-serif text-lg font-bold text-[#0A0B0F] dark:text-white">Growth Trajectory</h3>
                                <p className="text-[9px] font-black text-stone-400 uppercase tracking-widest">30-Day Market Pulse</p>
                            </div>
                            <div className="flex gap-1 bg-white dark:bg-black/20 p-1 rounded-sm border border-stone-200 dark:border-white/5">
                                {['7D', '30D', '1Y'].map(t => (
                                    <button key={t} className={`px-3 py-1 rounded-sm text-[9px] font-black uppercase tracking-widest transition-all ${t === '30D' ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black shadow-sm' : 'text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'}`}>{t}</button>
                                ))}
                            </div>
                        </div>
                        <div className="flex-1 w-full min-h-[320px] p-2 pb-0">
                           <AreaChart data={historicalData} />
                        </div>
                        <div className="p-4 bg-stone-50/30 dark:bg-black/10 border-t border-stone-50 dark:border-white/5">
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[8px] font-mono font-bold text-stone-400 uppercase tracking-widest">Live Sync Active • All Clusters Nominal</span>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="lg:col-span-4">
                    <Card className="h-full flex flex-col p-6 border-stone-200 dark:border-white/5 relative overflow-hidden">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8 shrink-0">
                             <div className="space-y-0.5 w-full text-center sm:text-left">
                                <h3 className="font-serif text-lg font-bold text-[#0A0B0F] dark:text-white flex items-center justify-center sm:justify-start gap-2">
                                    <PieIcon size={18} className="text-nobel-gold"/> Asset Allocation
                                </h3>
                                <p className="text-[9px] font-black text-stone-400 uppercase tracking-widest">Global Vault Spread</p>
                             </div>
                        </div>
                        
                        <div className="flex-1 flex flex-col items-center justify-center gap-8">
                            <div className="w-48 h-48 shrink-0 relative flex items-center justify-center">
                                <PieChart data={pieChartData} className="w-full h-full" />
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pt-4">
                                    <span className="text-[9px] text-stone-400 font-bold uppercase tracking-wider">Liquid</span>
                                    <span className="text-xs font-mono font-bold text-[#0A0B0F] dark:text-white">
                                        ${(portfolioValue / 1000).toFixed(1)}k
                                    </span>
                                </div>
                            </div>

                            <div className="w-full space-y-1">
                                {pieChartData.slice(0, 3).map(item => (
                                    item.name !== 'Empty' && (
                                        <div key={item.name} className="flex items-center justify-between p-2.5 rounded-sm bg-stone-50 dark:bg-white/[0.02] border border-stone-100 dark:border-white/5 hover:border-nobel-gold/20 transition-all group">
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                                                <span className="text-[10px] font-black text-stone-700 dark:text-stone-300 uppercase tracking-widest">{item.name}</span>
                                            </div>
                                            <div className="text-[10px] font-mono font-bold text-[#0A0B0F] dark:text-white">
                                                {((item.value / portfolioValue) * 100).toFixed(1)}%
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* 3. Dense Asset Ledger */}
            <Card noPadding className="overflow-hidden border-stone-200 dark:border-[#2D3142] shadow-sm">
              <div className="px-6 py-4 border-b border-stone-100 dark:border-white/5 bg-stone-50 dark:bg-black/20 flex flex-col sm:flex-row justify-between items-center text-center sm:text-left gap-4">
                <div className="space-y-0.5">
                    <h3 className="font-serif text-lg font-bold text-stone-900 dark:text-white">Active Ledger Assets</h3>
                    <p className="text-[9px] text-stone-400 uppercase font-black tracking-[0.2em]">Verified On-Chain Positions</p>
                </div>
                <div className="relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                    <input 
                        type="text" 
                        placeholder="Search vaults..." 
                        className="bg-white dark:bg-[#0A0B0F] border border-stone-200 dark:border-white/10 pl-9 pr-4 py-2 rounded-sm text-[10px] uppercase font-bold outline-none focus:border-nobel-gold transition-all"
                    />
                </div>
              </div>
              <div className="divide-y divide-stone-100 dark:divide-white/5 max-h-[400px] overflow-y-auto custom-scrollbar text-left font-sans">
                {balances.length > 0 ? (
                    balances.map(token => <AssetRow key={token.mint} token={token} />)
                ) : (
                    <div className="p-16 text-center text-stone-400">
                        <Database size={40} className="mx-auto mb-4 opacity-10" />
                        <p className="font-bold text-[10px] uppercase tracking-widest">No Interaction Data Found</p>
                    </div>
                )}
              </div>
              <div className="p-3 bg-stone-50/50 dark:bg-black/40 border-t border-stone-100 dark:border-white/5 flex justify-center">
                  <Button variant="ghost" size="sm" className="w-full text-[10px] font-black uppercase tracking-[0.3em] hover:text-nobel-gold">Export Analytics (CSV)</Button>
              </div>
            </Card>

        </div>
    );
};
